import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {ClickEventHandler} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipShortcutFormatter} from '../../../../libs/shared/ui/src/components/tooltips/tooltipShortcutFormatter';
import {VisuallyHidden} from '../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';

interface FloatingToolbarButtonProps {
  iconName: IconName;
  label: ReactNode;
  size?: VisualSizesEnum.LARGE | VisualSizesEnum.MEDIUM | VisualSizesEnum.SMALL;
  keyboardAction?: KeyboardActionsEnum;
  customKeyboardAction?: ReactNode;
  isActive?: boolean;
  onClick?: ClickEventHandler;
  shouldRenderLabelInline?: boolean;
  customMargin?: number;
}

/*
 * Style.
 */

const stylesBySize = {
  [VisualSizesEnum.SMALL]: css`
    min-height: 16px;
    margin: 0;
    border-radius: 4px;
    padding: 2px;
  `,
  [VisualSizesEnum.MEDIUM]: css`
    min-height: 16px;
    margin: 0;
    border-radius: 5px;
  `,
  [VisualSizesEnum.LARGE]: css`
    min-height: 22px;
    padding: 0 8px;
    margin: 0;
    border-radius: 5px;
    grid-gap: 8px;
  `,
  [VisualSizesEnum.EXTRA_LARGE]: css`
    padding: 5px;
    margin: 0 1px;
    border-radius: 6px;
    grid-gap: 5px;
  `,
};

const StyledButton = styled(ChromelessButton)<{
  $isActive: boolean | undefined;
  $size: VisualSizesEnum.LARGE | VisualSizesEnum.MEDIUM | VisualSizesEnum.SMALL;
}>`
  color: ${(p) => (p.$isActive ? p.theme.greys.white : p.theme.greys.shade40)};

  display: grid;
  font-weight: ${fontWeights.medium};
  grid-auto-flow: column;
  grid-auto-columns: auto;
  align-items: center;

  &:hover {
    color: ${(p) => p.theme.greys.white};
    background-color: ${(p) => p.theme.alphas.white20};
  }

  ${(p) => stylesBySize[p.$size]};
`;

const StyledCustomKeyboardActionSpan = styled.span`
  text-transform: capitalize;
`;

/*
 * Component.
 */

export const FloatingToolbarButton: FC<FloatingToolbarButtonProps> = (props) => (
  <TooltipCoordinator
    render={() => (props.shouldRenderLabelInline ? null : renderTooltip(props))}
    customMargin={props.customMargin ?? 9}
    placement="top"
  >
    <StyledButton
      $size={props.size ?? VisualSizesEnum.LARGE}
      $isActive={props.isActive}
      shouldPreventMouseDown
      onClick={props.onClick}
    >
      <Icon name={props.iconName} />
      {props.shouldRenderLabelInline ? props.label : <VisuallyHidden>{props.label}</VisuallyHidden>}
    </StyledButton>
  </TooltipCoordinator>
);

function renderTooltip(props: FloatingToolbarButtonProps) {
  return (
    <>
      {props.label} {renderTooltipKeyboardAction(props)}
    </>
  );
}

function renderTooltipKeyboardAction(props: FloatingToolbarButtonProps) {
  if (props.customKeyboardAction) {
    return <StyledCustomKeyboardActionSpan>[{props.customKeyboardAction}]</StyledCustomKeyboardActionSpan>;
  }

  if (props.keyboardAction) {
    return <TooltipShortcutFormatter action={props.keyboardAction} />;
  }

  return null;
}
